function unwrapValue(entry: any): unknown {
  if (entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
    return entry.value
  }
  return entry
}

function isExplicitNullTypedEntry(entry: any): boolean {
  return Boolean(
    entry &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      'value' in entry &&
      (entry as { value?: unknown }).value === null,
  )
}

/** Есть ли у поля полезное значение в текущей модели (после merge / в UniData). */
export function fieldEntryHasPresentableValue(entry: any): boolean {
  if (entry == null) return false
  const raw = unwrapValue(entry)
  if (raw == null) return false
  if (typeof raw === 'string') return raw.trim().length > 0
  if (typeof raw === 'number') return !Number.isNaN(raw)
  if (typeof raw === 'boolean') return true
  if (Array.isArray(raw)) return raw.length > 0
  if (typeof raw === 'object') {
    const rows = (raw as { rows?: unknown }).rows
    if (Array.isArray(rows)) return rows.length > 0
    if ('from' in raw || 'to' in raw) {
      const from = (raw as { from?: unknown }).from
      const to = (raw as { to?: unknown }).to
      const piece = (v: unknown) =>
        v != null && (typeof v !== 'string' || v.trim() !== '')
      return piece(from) || piece(to)
    }
    return Object.keys(raw).length > 0
  }
  return false
}

/** Typed UniDataTable (including empty `rows` / `empty: true`) — valid putFullData payload. */
export function fieldEntryIsUniDataTable(entry: any): boolean {
  if (entry == null) return false
  const entryType = String((entry as { type?: unknown }).type ?? '').toLowerCase()
  if (entryType === 'unidatatable' || entryType === 'array') {
    return true
  }
  const raw = unwrapValue(entry)
  if (raw != null && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
    return true
  }
  return false
}

export function fieldEntryLooksEmptyIncoming(entry: any): boolean {
  if (entry == null) return true
  const raw = unwrapValue(entry)
  if (raw == null) return true
  if (typeof raw === 'string') return raw.trim() === ''
  if (typeof raw === 'number') return false
  if (typeof raw === 'boolean') return false
  if (Array.isArray(raw)) return raw.length === 0
  if (typeof raw === 'object') {
    const rows = (raw as { rows?: unknown }).rows
    if (Array.isArray(rows)) return rows.length === 0
    if ('from' in raw || 'to' in raw) {
      const from = (raw as { from?: unknown }).from
      const to = (raw as { to?: unknown }).to
      const emptyPiece = (v: unknown) =>
        v == null || (typeof v === 'string' && v.trim() === '')
      return emptyPiece(from) && emptyPiece(to)
    }
    return Object.keys(raw).length === 0
  }
  return false
}

export function shouldPreserveFieldEntryOnEmptyIncoming(existingEntry: any, incomingEntry: any): boolean {
  if (isExplicitNullTypedEntry(incomingEntry)) {
    return false
  }
  if (incomingEntry && typeof incomingEntry === 'object' && !Array.isArray(incomingEntry)) {
    const type = String((incomingEntry as { type?: unknown }).type ?? '')
    if (type === 'UniDataTable' || type === 'UniDataTableFields') {
      const val = (incomingEntry as { value?: { empty?: unknown } }).value
      if (val && typeof val === 'object' && !Array.isArray(val) && typeof val.empty === 'boolean') {
        return false
      }
    }
  }
  return (
    fieldEntryHasPresentableValue(existingEntry) && fieldEntryLooksEmptyIncoming(incomingEntry)
  )
}

export type FieldStatePreserveHint = {
  element_name?: string
  is_refill?: boolean
  is_empty?: boolean
}

export function fieldHasRefillOrEmptyState(
  states: FieldStatePreserveHint[] | undefined,
  fieldName: string,
): boolean {
  const name = String(fieldName ?? '').trim()
  if (!name || !Array.isArray(states)) return false
  return states.some((state) => {
    if (String(state?.element_name ?? '').trim() !== name) return false
    return state.is_refill === true || state.is_empty === true
  })
}

export function shouldPreserveFieldEntryOnEmptyIncomingForStates(
  fieldName: string,
  existingEntry: unknown,
  incomingEntry: unknown,
  states: FieldStatePreserveHint[] | undefined,
): boolean {
  if (fieldHasRefillOrEmptyState(states, fieldName)) {
    return false
  }
  return shouldPreserveFieldEntryOnEmptyIncoming(existingEntry, incomingEntry)
}
